import { Component, inject, OnInit, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { TranslateModule } from "@ngx-translate/core";
import { firstValueFrom } from "rxjs";
import { environment } from "src/environments/environment";
import { TenantContextService, TenantMembership, OrgType } from "src/app/core/services/tenant-context.service";

interface OrgListing {
  id: number;
  slug: string;
  nameAr: string;
  nameEn?: string;
  type: OrgType;
}

/**
 * Super-admin impersonation switcher. Renders nothing for normal
 * users. For super-admins, shows a dropdown of every active org and
 * sets TenantContext.active on selection so downstream requests go
 * out with the impersonated X-Org-Id.
 */
@Component({
  selector: "app-tenant-switcher",
  standalone: true,
  imports: [CommonModule, TranslateModule, MatButtonModule, MatIconModule, MatMenuModule],
  template: `
    @if (ctx.isSuperAdmin()) {
    <button mat-stroked-button [matMenuTriggerFor]="menu" class="tenant-switcher-btn">
      <mat-icon>business</mat-icon>
      <span>{{ ctx.active()?.nameAr || ("tenant-switcher.label" | translate) }}</span>
      <mat-icon class="chevron">expand_more</mat-icon>
    </button>
    <mat-menu #menu="matMenu">
      @for (org of orgs(); track org.id) {
      <button mat-menu-item (click)="impersonate(org)">
        <mat-icon>{{ org.type === "maqraa" ? "mosque" : "account_balance" }}</mat-icon>
        <span>{{ org.nameAr }}</span>
        @if (ctx.active()?.organizationId === org.id) {
        <mat-icon class="active-check">check</mat-icon>
        }
      </button>
      }
    </mat-menu>
    }
  `,
  styles: [
    `
      .tenant-switcher-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        height: 36px;
        .chevron {
          font-size: 18px;
          width: 18px;
          height: 18px;
          margin-inline-start: 0.25rem;
          opacity: 0.6;
        }
      }
      .active-check {
        margin-inline-start: auto;
        color: var(--primary-color);
      }
    `,
  ],
})
export class TenantSwitcherComponent implements OnInit {
  readonly ctx = inject(TenantContextService);
  private readonly http = inject(HttpClient);

  readonly orgs = signal<OrgListing[]>([]);

  async ngOnInit() {
    if (!this.ctx.isSuperAdmin()) return;
    try {
      const list = await firstValueFrom(
        this.http.get<OrgListing[]>(`${environment.meetApiHost}api/admin/organizations`)
      );
      this.orgs.set(list ?? []);
    } catch {
      this.orgs.set([]);
    }
  }

  impersonate(org: OrgListing) {
    // Synthesize a membership record for the impersonated tenant.
    // Backend tenant-resolver already bypasses membership checks for
    // super-admins, so no server round-trip is needed here.
    const membership: TenantMembership = {
      organizationId: org.id,
      slug: org.slug,
      nameAr: org.nameAr,
      nameEn: org.nameEn,
      type: org.type,
      branding: null,
      role: "super_admin",
      isPrimary: false,
    };
    this.ctx.setActive(membership);
    // Full reload to flush every in-memory cache that might hold
    // previous-tenant data keyed on the old X-Org-Id.
    window.location.reload();
  }
}
